import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Modal,
  Stack,
  Button,
  Group,
  ScrollArea,
  Loader,
  Center,
  Text,
  Divider,
  Select,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconInfoCircle,
  IconPrinter,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { partitionAddendumReportPhotos } from "../../utils/addendumReportPhotos";
import {
  getReportPreview,
  issueProjectedReportPreview,
  generatePdfBlobUrl,
  acknowledgeReportPreview,
  ReportContentChangedError,
  ReportPreviewEvidence,
  ReportPreviewData,
} from "../../services/signOff.service";
import {
  captureReportHtml,
  collectPageStylesheets,
} from "../../utils/reportHtml";
import {
  pathologistReviewService,
  type PathologistComment,
} from "../../services/workflows.service";
import { DocumentView } from "../pathologist-review/DocumentView";
import { casesService, CaseFile } from "../../services/cases.service";
import {
  caseImagesService,
  CaseImage,
  REPORT_IMAGE_NOT_AVAILABLE_MESSAGE,
} from "../../services/caseImages.service";
import { useActiveReportTemplates } from "../../hooks/admin/useReportTemplates";
import { useDiagnosisCategories } from "../../hooks/useWorkflows";
import { useDocumentViewData } from "../../hooks/useDocumentView";
import { documentViewService } from "../../services/documentView.service";
import { deriveSignedVersionCodeDisplays } from "../../utils/signedReportVersion";
import { ReportAttachmentPages } from "./ReportAttachmentPages";
import { isInRenderMode } from "../../lib/renderModeFlag";
import { internalRenderService } from "../../services/internalRender.service";
import {
  isRenderableReportFile,
  ReportFilePage,
  type ReportFileAttachment,
} from "./ReportFilePage";
import type {
  OrganMapPhoto,
  ReportImageAttachment,
} from "../pathologist-review/ReportPhotoContent";
import { useReportAttachmentUrls } from "./useReportAttachmentUrls";
import {
  clearInternalRenderFailure,
  signalInternalRenderFailure,
} from "../../lib/internalRenderSentinel";
import { isImageMimeType } from "../../utils/reportAttachments";
import {
  hasMissingExpectedOrganMapImage,
  mergeAuthoritativeOrganMapData,
} from "../../utils/reportOrganMapSpecimens";
import { REPORT_PAGE_LAYOUT } from "../../utils/reportPageLayout";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import type {
  DocumentViewResponse,
  TemplateSection,
} from "../../types/reportTemplate";
import { historicalPatientName } from "../pathologist-review/legacyHistoricalReportLayout";
import { projectExternalBaselineStains } from "../../utils/externalBaselineReportProjection";
import type { InternalRenderAttachments } from "../../services/internalRender.service";
import type { ReportComposition } from "../../utils/reportComposition";
import { ReportCompositionDocument } from "./ReportCompositionDocument";

function showReportImageFailure(): void {
  notifications.show({
    title: "Report photo unavailable",
    message: REPORT_IMAGE_NOT_AVAILABLE_MESSAGE,
    color: "red",
  });
}

function reportPhotoFromFile(
  file: ReportFileAttachment
): ReportImageAttachment {
  return {
    id: file.id,
    caption: file.label?.trim() || undefined,
    fileName: file.name,
  };
}

function mergeUrlMaps(
  first: ReadonlyMap<string, string>,
  second: ReadonlyMap<string, string>
): Map<string, string> {
  return new Map(Array.from(first).concat(Array.from(second)));
}

function withoutReportFooter(
  documentViewData: DocumentViewResponse
): DocumentViewResponse {
  return {
    ...documentViewData,
    template: {
      ...documentViewData.template,
      templateContent: {
        ...documentViewData.template.templateContent,
        sections: documentViewData.template.templateContent.sections.filter(
          (section) => section.type !== "disclaimers"
        ),
      },
    },
  };
}

function withReportFooterAfterAddendum(
  documentViewData: DocumentViewResponse,
  signedDocumentViewData: DocumentViewResponse
): DocumentViewResponse {
  const activeSections = documentViewData.template.templateContent.sections;
  const reportFooterSection =
    activeSections.find((section) => section.type === "disclaimers") ??
    signedDocumentViewData.template.templateContent.sections.find(
      (section) => section.type === "disclaimers"
    );
  const hasAddendum = documentViewData.addendums.some(
    (entry) => entry.signOffType === "addendum"
  );
  if (!reportFooterSection || !hasAddendum) return documentViewData;

  return {
    ...documentViewData,
    template: {
      ...documentViewData.template,
      templateContent: {
        ...documentViewData.template.templateContent,
        sections: [
          ...activeSections.filter((section) => section.type !== "disclaimers"),
          reportFooterSection,
        ],
      },
    },
  };
}

interface SpecimenDiagnosisData {
  specimenId: string;
  diagnosisText: string;
  microscopicDescription: string;
  category?: string | null;
}

interface SpecimenGrossingData {
  specimenId: string;
  grossDescription: string;
}

interface Specimen {
  id: string;
  number: string;
  sourceName: string;
  collectionMethod?: string | null;
  findings?: string | null;
  // Organ map fields
  organMapTemplateId?: string | null;
  organMapTemplateName?: string | null;
  organMapImageUrl?: string | null;
  organMapWidth?: number | null;
  organMapHeight?: number | null;
  subsiteName?: string | null;
  subsitePositionX?: number | null;
  subsitePositionY?: number | null;
}

const mapPreviewDiagnosisToSpecimen = (
  diagnosis: ReportPreviewData["diagnoses"][number],
  index: number
): Specimen => ({
  id: `preview-specimen-${index}`,
  number: diagnosis.specimenLabel,
  sourceName: diagnosis.source,
  collectionMethod: diagnosis.procedure,
  findings: diagnosis.findings,
  organMapTemplateId: null,
  organMapTemplateName: null,
  organMapImageUrl: null,
  organMapWidth: null,
  organMapHeight: null,
  subsiteName: null,
  subsitePositionX: null,
  subsitePositionY: null,
});

const getPreviewSpecimens = (preview?: ReportPreviewData): Specimen[] => {
  if (!preview) return [];
  if (preview.specimens?.length) return preview.specimens;
  return preview.diagnoses.map(mapPreviewDiagnosisToSpecimen);
};

interface LiveEditData {
  clinicalHistoryNotes?: string;
  indications?: string;
  impressions?: string;
  pathologistComment?: string;
  frozenInterpretation?: string;
  procedure?: string;
  specimens?: Specimen[];
  diagnosisDataMap?: Map<string, SpecimenDiagnosisData>;
  grossingDataMap?: Map<string, SpecimenGrossingData>;
  liveCptDisplay?: string[] | null;
  liveIcdDisplay?: string[] | null;
}

interface ReportDiagnosisCategory {
  name: string;
  markerShape: string | null;
  markerColor: string | null;
}

interface ReportPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onSignOut?: (previewEvidence?: ReportPreviewEvidence) => void | Promise<void>;
  onAcknowledgeReview?: (
    contentVersion: ReportPreviewEvidence["contentVersion"]
  ) => Promise<ReportPreviewEvidence>;
  onReviewContentChanged?: () => void | Promise<void>;
  reviewRequired?: boolean;
  signOutPending?: boolean;
  allowSignOutWhenFinalized?: boolean;
  simulateMainSignature?: boolean;
  showPrintAction?: boolean;
  signOutLabel?: string;
  title?: string;
  clinicalSideEffectNotice?: string;
  liveData?: LiveEditData;
  readOnly?: boolean;
  /** Notifies the parent whenever the pathologist (or the auto-select effect)
   *  picks a template. The parent persists this on sign-off so the backend
   *  renderer reproduces the same layout. */
  onTemplateIdChange?: (templateId: string | undefined) => void;
  /** Render the document body inline (no Modal chrome, no buttons) for the
   *  headless Puppeteer renderer. When all data + images have resolved and
   *  pagination has settled, sets `<html data-render-complete="true">`. */
  renderForCapture?: boolean;
  /** Signed report version to render. Used by headless capture to load the
   * immutable diagnosis snapshot instead of current case diagnoses. */
  reportVersion?: number;
  /** Force-pick a specific template instead of auto-selecting (used by the
   *  internal-render route to honour the templateId stored on sign-off). */
  forceTemplateId?: string;
  /** Purpose-scoped marker projection supplied by the internal render context. */
  diagnosisCategoriesOverride?: ReportDiagnosisCategory[];
  documentViewDataOverride?: DocumentViewResponse;
  previewDataOverride?: ReportPreviewData;
  pathologistCommentsOverride?: PathologistComment[];
  reportAttachmentsOverride?: InternalRenderAttachments;
  reportCompositionOverride?: ReportComposition;
  interstitialReportFile?: {
    file: ReportFileAttachment;
    url: string;
  };
  trailingReportFile?: {
    file: ReportFileAttachment;
    url: string;
  };
  signedDocumentViewData?: DocumentViewResponse;
  hasExternalBaseline?: boolean;
  /** Optional same-flow navigation rendered in the standard preview footer. */
  onBack?: () => void;
  /** Corrections omit unavailable maps instead of blocking the corrected report. */
  blockOnUnavailableOrganMaps?: boolean;
}

export function ReportPreviewModal({
  opened,
  onClose,
  caseId,
  onSignOut,
  onAcknowledgeReview,
  onReviewContentChanged,
  reviewRequired = false,
  signOutPending = false,
  allowSignOutWhenFinalized = false,
  simulateMainSignature = true,
  showPrintAction = true,
  signOutLabel,
  title = "Report Preview",
  clinicalSideEffectNotice,
  liveData,
  readOnly,
  onTemplateIdChange,
  renderForCapture,
  reportVersion,
  forceTemplateId,
  diagnosisCategoriesOverride,
  documentViewDataOverride,
  previewDataOverride,
  pathologistCommentsOverride,
  reportAttachmentsOverride,
  reportCompositionOverride,
  interstitialReportFile,
  trailingReportFile,
  signedDocumentViewData,
  hasExternalBaseline = false,
  onBack,
  blockOnUnavailableOrganMaps = true,
}: ReportPreviewModalProps) {
  // The modal's queries are gated on `opened`. The internal-render route mounts
  // it inline (no Modal), so treat capture mode as always "open" so data fetches.
  const queriesEnabled = opened || !!renderForCapture;
  const renderMode = isInRenderMode();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isAcknowledgingReview, setIsAcknowledgingReview] = useState(false);
  const [externalReportFileLoaded, setExternalReportFileLoaded] =
    useState(false);
  const [externalReportFileFailed, setExternalReportFileFailed] =
    useState(false);
  const [externalReportFileAttempt, setExternalReportFileAttempt] = useState(0);
  const [reviewError, setReviewError] = useState<{
    kind: "info" | "error";
    title: string;
    message: string;
  } | null>(null);
  const [mainReportPageCount, setMainReportPageCount] = useState<number | null>(
    null
  );
  const [signedReportPageCount, setSignedReportPageCount] = useState<
    number | null
  >(null);
  const [externalReportPageCount, setExternalReportPageCount] = useState<
    number | null
  >(null);
  const hasExternalReportFile = useMemo(
    () => Boolean(interstitialReportFile || trailingReportFile),
    [interstitialReportFile, trailingReportFile]
  );
  const resolvedSignedReportPageCount = useMemo(
    () => (signedDocumentViewData ? signedReportPageCount : 0),
    [signedDocumentViewData, signedReportPageCount]
  );
  const resolvedExternalReportPageCount = useMemo(
    () => (hasExternalReportFile ? externalReportPageCount : 0),
    [externalReportPageCount, hasExternalReportFile]
  );
  const legacyPaginationSettled = useMemo(
    () =>
      mainReportPageCount !== null &&
      resolvedSignedReportPageCount !== null &&
      resolvedExternalReportPageCount !== null,
    [
      mainReportPageCount,
      resolvedExternalReportPageCount,
      resolvedSignedReportPageCount,
    ]
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const reportDocumentRef = useRef<HTMLDivElement>(null);
  const resetPaginationState = useCallback(() => {
    setMainReportPageCount(null);
  }, []);
  const handlePaginationSettled = useCallback((pageCount: number) => {
    setMainReportPageCount(pageCount);
  }, []);
  const handleSignedPaginationSettled = useCallback((pageCount: number) => {
    setSignedReportPageCount(pageCount);
  }, []);
  const handleSignedPaginationPending = useCallback(() => {
    setSignedReportPageCount(null);
  }, []);
  const handlePaginationPending = resetPaginationState;
  const handlePaginationFailed = useCallback(() => {
    resetPaginationState();
    if (renderForCapture) signalInternalRenderFailure("pagination");
  }, [renderForCapture, resetPaginationState]);
  const handleExternalReportFileLoadComplete = useCallback(
    (_fileId: string, pageCount = 1) => {
      setExternalReportPageCount(pageCount);
      setExternalReportFileLoaded(true);
      setExternalReportFileFailed(false);
    },
    []
  );
  const handleExternalReportFileLoadError = useCallback(() => {
    setExternalReportPageCount(null);
    setExternalReportFileLoaded(false);
    setExternalReportFileFailed(true);
  }, []);
  const handleRetryExternalReportFile = useCallback(() => {
    setExternalReportPageCount(null);
    setExternalReportFileLoaded(false);
    setExternalReportFileFailed(false);
    setExternalReportFileAttempt((attempt) => attempt + 1);
  }, []);
  const handleTemplateChange = useCallback(
    (templateId: string | null) => {
      resetPaginationState();
      setReviewError(null);
      setSelectedTemplateId(templateId || "");
    },
    [resetPaginationState]
  );
  const activeTemplatesQuery = useActiveReportTemplates(!renderMode);
  const activeTemplates = useMemo(
    () => activeTemplatesQuery.data ?? [],
    [activeTemplatesQuery.data]
  );
  const categoryQuery = useDiagnosisCategories(!renderMode);
  const diagnosisCategories =
    diagnosisCategoriesOverride ?? categoryQuery.data ?? [];
  const isCategoriesLoading =
    diagnosisCategoriesOverride === undefined && categoryQuery.isLoading;
  const categoriesSettled =
    diagnosisCategoriesOverride !== undefined || categoryQuery.isSuccess;
  const usesInitialPreviewProjection = useMemo(
    () =>
      !documentViewDataOverride &&
      reportVersion === undefined &&
      !renderForCapture,
    [documentViewDataOverride, renderForCapture, reportVersion]
  );
  const initialTemplateSettled = useMemo(
    () =>
      !usesInitialPreviewProjection ||
      (activeTemplatesQuery.isSuccess &&
        (Boolean(selectedTemplateId) || activeTemplates.length === 0)),
    [
      activeTemplates.length,
      activeTemplatesQuery.isSuccess,
      selectedTemplateId,
      usesInitialPreviewProjection,
    ]
  );
  const loadInitialPreviewProjection = useCallback(
    () =>
      documentViewService.fetchInitialPreviewProjection(
        caseId,
        selectedTemplateId || undefined
      ),
    [caseId, selectedTemplateId]
  );
  const initialProjectionQuery = useQuery<DocumentViewResponse>({
    queryKey: ["initial-preview-projection", caseId, selectedTemplateId],
    queryFn: loadInitialPreviewProjection,
    enabled:
      queriesEnabled &&
      !!caseId &&
      usesInitialPreviewProjection &&
      initialTemplateSettled,
  });
  const documentViewQuery = useDocumentViewData(
    caseId,
    selectedTemplateId || undefined,
    reportVersion,
    !documentViewDataOverride && !usesInitialPreviewProjection
  );
  const documentViewData = useMemo(
    () =>
      documentViewDataOverride ||
      (usesInitialPreviewProjection
        ? initialProjectionQuery.data
        : documentViewQuery.data),
    [
      documentViewDataOverride,
      documentViewQuery.data,
      initialProjectionQuery.data,
      usesInitialPreviewProjection,
    ]
  );
  const documentViewSettled = useMemo(
    () =>
      Boolean(documentViewDataOverride) ||
      (usesInitialPreviewProjection
        ? initialProjectionQuery.isSuccess
        : documentViewQuery.isSuccess),
    [
      documentViewDataOverride,
      documentViewQuery.isSuccess,
      initialProjectionQuery.isSuccess,
      usesInitialPreviewProjection,
    ]
  );
  const canSelectTemplate = useMemo(
    () =>
      !documentViewData?.legacyBaseline &&
      (usesInitialPreviewProjection || documentViewSettled),
    [
      documentViewData?.legacyBaseline,
      documentViewSettled,
      usesInitialPreviewProjection,
    ]
  );
  useEffect(() => {
    if (!canSelectTemplate) return;
    // When the caller force-pins a templateId (e.g. the internal-render route
    // honouring the templateId stored on sign-off), use it verbatim and skip
    // the auto-select heuristic.
    if (forceTemplateId) {
      if (forceTemplateId !== selectedTemplateId) {
        handleTemplateChange(forceTemplateId);
      }
      return;
    }

    if (selectedTemplateId || activeTemplates.length === 0) return;

    // If any specimen has an organ map template, prefer a report template with organ_map section
    const hasOrganMapSpecimens = liveData?.specimens?.some(
      (s) => s.organMapTemplateId
    );
    if (hasOrganMapSpecimens) {
      const organMapTemplate = activeTemplates.find((t: any) => {
        const sections = t.templateContent?.sections || [];
        return sections.some(
          (sec: any) =>
            sec.type === "organ_map" || sec.type === "diagnostic_map"
        );
      });
      if (organMapTemplate) {
        handleTemplateChange(organMapTemplate.id);
        return;
      }
    }

    const defaultTpl = activeTemplates.find((t: any) => t.isDefault);
    handleTemplateChange(defaultTpl?.id || activeTemplates[0]?.id || "");
  }, [
    activeTemplates,
    canSelectTemplate,
    selectedTemplateId,
    liveData?.specimens,
    forceTemplateId,
    handleTemplateChange,
  ]);

  // Notify parent whenever the active templateId changes so it can persist it
  // with sign-off. Empty string => undefined (auto-select on the backend).
  useEffect(() => {
    onTemplateIdChange?.(selectedTemplateId || undefined);
  }, [selectedTemplateId, onTemplateIdChange]);

  // Fetch existing report preview data for case/specimen metadata
  const loadReportPreview = useCallback(() => {
    if (usesInitialPreviewProjection && initialProjectionQuery.data) {
      return issueProjectedReportPreview(
        caseId,
        initialProjectionQuery.data,
        selectedTemplateId || undefined
      );
    }
    return getReportPreview(
      caseId,
      reportVersion,
      selectedTemplateId || undefined
    );
  }, [
    caseId,
    initialProjectionQuery.data,
    reportVersion,
    selectedTemplateId,
    usesInitialPreviewProjection,
  ]);
  const previewQuery = useQuery<ReportPreviewData>({
    queryKey: ["report-preview", caseId, reportVersion, selectedTemplateId],
    queryFn: loadReportPreview,
    enabled:
      queriesEnabled &&
      !!caseId &&
      !previewDataOverride &&
      (!usesInitialPreviewProjection || initialProjectionQuery.isSuccess),
  });
  const preview = previewDataOverride || previewQuery.data;
  const composition =
    reportCompositionOverride ||
    preview?.review?.contentVersion.reportComposition;
  const paginationSettled = composition
    ? mainReportPageCount !== null
    : legacyPaginationSettled;
  const isLoading = useMemo(
    () =>
      (!previewDataOverride && previewQuery.isLoading) ||
      (usesInitialPreviewProjection && initialProjectionQuery.isLoading) ||
      (usesInitialPreviewProjection && activeTemplatesQuery.isLoading),
    [
      activeTemplatesQuery.isLoading,
      initialProjectionQuery.isLoading,
      previewDataOverride,
      previewQuery.isLoading,
      usesInitialPreviewProjection,
    ]
  );
  const error = useMemo(
    () =>
      (!previewDataOverride && previewQuery.error) ||
      (usesInitialPreviewProjection && initialProjectionQuery.error),
    [
      initialProjectionQuery.error,
      previewDataOverride,
      previewQuery.error,
      usesInitialPreviewProjection,
    ]
  );
  const { refetch: refetchPreview } = previewQuery;
  const reviewContentKey = preview?.review?.contentVersion.fingerprint;
  const effectiveReviewRequired =
    preview?.review?.reviewRequired ?? reviewRequired;
  const projectedDocumentViewData = useMemo(() => {
    const projectedDocumentViewData = projectExternalBaselineStains(
      documentViewData,
      preview,
      hasExternalBaseline || Boolean(trailingReportFile)
    );
    if (!projectedDocumentViewData || !signedDocumentViewData) {
      return projectedDocumentViewData;
    }
    return withReportFooterAfterAddendum(
      projectedDocumentViewData,
      signedDocumentViewData
    );
  }, [
    documentViewData,
    hasExternalBaseline,
    preview,
    signedDocumentViewData,
    trailingReportFile,
  ]);
  const effectiveSignedDocumentViewData = useMemo(() => {
    if (
      !signedDocumentViewData ||
      projectedDocumentViewData === documentViewData
    ) {
      return signedDocumentViewData;
    }
    return projectedDocumentViewData?.addendums.some(
      (entry) => entry.signOffType === "addendum"
    )
      ? withoutReportFooter(signedDocumentViewData)
      : signedDocumentViewData;
  }, [documentViewData, projectedDocumentViewData, signedDocumentViewData]);
  const effectiveDocumentViewData = useMemo(() => {
    const simulation = preview?.review?.signatureSimulation;
    if (
      !projectedDocumentViewData ||
      !effectiveReviewRequired ||
      !simulation ||
      !simulateMainSignature
    ) {
      return projectedDocumentViewData;
    }
    return {
      ...projectedDocumentViewData,
      pathologistInfo: {
        ...projectedDocumentViewData.pathologistInfo,
        name: simulation.signerName,
        credentials: simulation.credentials,
      },
      signatureImageUrl: null,
    };
  }, [
    effectiveReviewRequired,
    preview,
    projectedDocumentViewData,
    simulateMainSignature,
  ]);
  const headerSection = useMemo<TemplateSection | undefined>(
    () =>
      effectiveDocumentViewData?.legacyBaseline
        ? { type: "header", label: "" }
        : effectiveDocumentViewData?.template.templateContent.sections.find(
            (section) => section.type === "header"
          ),
    [effectiveDocumentViewData]
  );

  // Fetch live pathologist comments for preview
  const commentsQuery = useQuery<PathologistComment[]>({
    queryKey: ["pathologist-comments-preview", caseId, reviewContentKey],
    queryFn: () => pathologistReviewService.getPathologistComments(caseId),
    enabled: queriesEnabled && !!caseId && !pathologistCommentsOverride,
  });
  const pathologistComments =
    pathologistCommentsOverride ?? commentsQuery.data ?? [];

  const filesQuery = useQuery<CaseFile[]>({
    queryKey: ["case-files-report", caseId, reviewContentKey],
    queryFn: () => casesService.getReportFiles(caseId),
    enabled: queriesEnabled && !!caseId && !renderMode,
  });

  const imagesQuery = useQuery<CaseImage[]>({
    queryKey: ["case-images-report", caseId, reviewContentKey],
    queryFn: () => caseImagesService.getImages(caseId),
    enabled: queriesEnabled && !!caseId && !renderMode,
  });

  const renderAttachmentsQuery = useQuery({
    queryKey: ["internal-render-attachments", caseId, reviewContentKey],
    queryFn: internalRenderService.getAttachments,
    enabled:
      queriesEnabled && !!caseId && renderMode && !reportAttachmentsOverride,
    retry: false,
  });

  const selectedReportFiles = useMemo<ReportFileAttachment[]>(
    () =>
      renderMode
        ? reportAttachmentsOverride?.files ||
          renderAttachmentsQuery.data?.files ||
          []
        : (filesQuery.data || []).filter((file) => file.includeInReport),
    [
      filesQuery.data,
      renderAttachmentsQuery.data?.files,
      renderMode,
      reportAttachmentsOverride?.files,
    ]
  );
  const baseReportFiles = useMemo(
    () =>
      !composition && !renderMode && signedDocumentViewData
        ? selectedReportFiles.filter((file) => file.finalizedInReport)
        : [],
    [composition, renderMode, selectedReportFiles, signedDocumentViewData]
  );
  const reportFiles = useMemo(
    () =>
      baseReportFiles.length > 0
        ? selectedReportFiles.filter((file) => !file.finalizedInReport)
        : selectedReportFiles,
    [baseReportFiles, selectedReportFiles]
  );
  const reportImages = useMemo<ReportImageAttachment[]>(
    () =>
      renderMode
        ? reportAttachmentsOverride?.images ||
          renderAttachmentsQuery.data?.images ||
          []
        : (imagesQuery.data || []).filter((image) => image.includeInReport),
    [
      imagesQuery.data,
      renderAttachmentsQuery.data?.images,
      renderMode,
      reportAttachmentsOverride?.images,
    ]
  );
  const reportPhotoFiles = useMemo(
    () => selectedReportFiles.filter((file) => isImageMimeType(file.mimeType)),
    [selectedReportFiles]
  );
  const reportDocumentFiles = useMemo(
    () => reportFiles.filter((file) => !isImageMimeType(file.mimeType)),
    [reportFiles]
  );
  const baseReportDocumentFiles = useMemo(
    () => baseReportFiles.filter((file) => !isImageMimeType(file.mimeType)),
    [baseReportFiles]
  );
  const allReportDocumentFiles = useMemo(
    () => [...baseReportDocumentFiles, ...reportDocumentFiles],
    [baseReportDocumentFiles, reportDocumentFiles]
  );
  const [filePageCounts, setFilePageCounts] = useState<Record<string, number>>(
    {}
  );
  const baseAttachmentPageCount = useMemo(
    () =>
      baseReportDocumentFiles.reduce(
        (total, file) => total + (filePageCounts[file.id] ?? 1),
        0
      ),
    [baseReportDocumentFiles, filePageCounts]
  );
  const currentAttachmentPageCount = useMemo(
    () =>
      reportDocumentFiles.reduce(
        (total, file) => total + (filePageCounts[file.id] ?? 1),
        0
      ),
    [filePageCounts, reportDocumentFiles]
  );
  const selectedReportPhotos = useMemo(
    () => [...reportImages, ...reportPhotoFiles.map(reportPhotoFromFile)],
    [reportImages, reportPhotoFiles]
  );

  const {
    imageQueries: imageUrlQueries,
    fileQueries: fileUrlQueries,
    resolvedImageUrls,
    resolvedFileUrls,
    queryFailedImageIds,
    failedFileIds,
  } = useReportAttachmentUrls({
    caseId,
    contentVersionKey: reviewContentKey,
    images: reportImages,
    files: selectedReportFiles,
    enabled: queriesEnabled,
    mode: renderMode ? "internal" : "regular",
  });
  const resolvedReportPhotoUrls = useMemo(
    () => mergeUrlMaps(resolvedImageUrls, resolvedFileUrls),
    [resolvedFileUrls, resolvedImageUrls]
  );
  const reportPhotoFileIds = useMemo(
    () => new Set(reportPhotoFiles.map((file) => file.id)),
    [reportPhotoFiles]
  );
  const queryFailedReportPhotoIds = useMemo(
    () =>
      new Set(
        Array.from(queryFailedImageIds).concat(
          Array.from(failedFileIds).filter((id) => reportPhotoFileIds.has(id))
        )
      ),
    [failedFileIds, queryFailedImageIds, reportPhotoFileIds]
  );

  const [decodeFailedImageIds, setDecodeFailedImageIds] = useState<Set<string>>(
    new Set()
  );
  const [failedFileContentIds, setFailedFileContentIds] = useState<Set<string>>(
    new Set()
  );
  const [loadedFileContentIds, setLoadedFileContentIds] = useState<Set<string>>(
    new Set()
  );

  const failedImageIds = useMemo(
    () =>
      new Set(
        Array.from(queryFailedReportPhotoIds).concat(
          Array.from(decodeFailedImageIds)
        )
      ),
    [decodeFailedImageIds, queryFailedReportPhotoIds]
  );
  const resolvedFailedFileIds = useMemo(
    () =>
      new Set(
        Array.from(failedFileIds).concat(Array.from(failedFileContentIds))
      ),
    [failedFileContentIds, failedFileIds]
  );

  const attachmentListError = renderMode
    ? reportAttachmentsOverride
      ? null
      : renderAttachmentsQuery.error
    : filesQuery.error || imagesQuery.error;
  const captureDataError =
    (!previewDataOverride ? error : null) ||
    (!pathologistCommentsOverride ? commentsQuery.error : null) ||
    (!documentViewDataOverride ? documentViewQuery.error : null) ||
    (diagnosisCategoriesOverride === undefined ? categoryQuery.error : null);

  const handleImageLoadError = useCallback(
    (imageId: string) => {
      setDecodeFailedImageIds((previous) => new Set(previous).add(imageId));
      if (renderForCapture) signalInternalRenderFailure("report-photo");
      else showReportImageFailure();
    },
    [renderForCapture]
  );

  const handleRetryImage = useCallback(
    (imageId: string) => {
      setDecodeFailedImageIds((previous) => {
        const next = new Set(previous);
        next.delete(imageId);
        return next;
      });
      const imageIndex = reportImages.findIndex(
        (image) => image.id === imageId
      );
      if (imageIndex >= 0) {
        void imageUrlQueries[imageIndex]?.refetch();
        return;
      }
      const fileIndex = selectedReportFiles.findIndex(
        (file) => file.id === imageId
      );
      void fileUrlQueries[fileIndex]?.refetch();
    },
    [fileUrlQueries, imageUrlQueries, reportImages, selectedReportFiles]
  );

  const handleRetryFile = useCallback(
    (fileId: string) => {
      setFailedFileContentIds((previous) => {
        const next = new Set(previous);
        next.delete(fileId);
        return next;
      });
      setLoadedFileContentIds((previous) => {
        const next = new Set(previous);
        next.delete(fileId);
        return next;
      });
      setFilePageCounts((previous) => {
        const next = { ...previous };
        delete next[fileId];
        return next;
      });
      const index = selectedReportFiles.findIndex((file) => file.id === fileId);
      void fileUrlQueries[index]?.refetch();
    },
    [fileUrlQueries, selectedReportFiles]
  );

  const handleFileLoadError = useCallback((fileId: string) => {
    setFailedFileContentIds((previous) => new Set(previous).add(fileId));
    setFilePageCounts((previous) => ({ ...previous, [fileId]: 1 }));
  }, []);

  const handleFileLoadComplete = useCallback(
    (fileId: string, pageCount = 1) => {
      setFilePageCounts((previous) => ({
        ...previous,
        [fileId]: Math.max(1, pageCount),
      }));
      setLoadedFileContentIds((previous) => new Set(previous).add(fileId));
    },
    []
  );

  const handleRetryAttachmentList = useCallback(() => {
    if (renderMode) {
      void renderAttachmentsQuery.refetch();
      return;
    }
    void filesQuery.refetch();
    void imagesQuery.refetch();
  }, [filesQuery, imagesQuery, renderAttachmentsQuery, renderMode]);
  const handleRetryReportData = useCallback(() => {
    resetPaginationState();
    if (onReviewContentChanged) {
      void onReviewContentChanged();
      return;
    }
    void refetchPreview();
    void documentViewQuery.refetch();
  }, [
    documentViewQuery,
    onReviewContentChanged,
    refetchPreview,
    resetPaginationState,
  ]);

  useEffect(() => {
    clearInternalRenderFailure();
    return () => {
      clearInternalRenderFailure();
    };
  }, []);

  useEffect(() => {
    if (!renderForCapture) return;
    if (captureDataError) {
      signalInternalRenderFailure();
    } else if (failedImageIds.size) {
      signalInternalRenderFailure("report-photo");
    } else if (attachmentListError || resolvedFailedFileIds.size) {
      signalInternalRenderFailure("report-attachment");
    }
  }, [
    attachmentListError,
    captureDataError,
    failedImageIds,
    renderForCapture,
    resolvedFailedFileIds,
  ]);

  const captureCurrentReportHtml = useCallback(async (): Promise<string> => {
    if (!reportDocumentRef.current) {
      throw new Error("Report content is not ready yet. Please try again.");
    }
    const caseNumber = preview?.caseNumber || caseId;
    return captureReportHtml(reportDocumentRef.current, {
      title: `Pathology Report - ${caseNumber}`,
      facilityName: preview?.facility || "",
      pageStylesheets: collectPageStylesheets(),
    });
  }, [caseId, preview]);

  const handlePrint = useCallback(async () => {
    setIsPrinting(true);
    // Open a tab immediately (user gesture) to avoid popup blockers,
    // show a loading message instead of a blank page while Puppeteer runs.
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      const s = printWindow.document.createElement("style");
      s.textContent = `body{margin:0;display:flex;flex-direction:column;align-items:center;
        justify-content:center;height:100vh;font-family:-apple-system,sans-serif;
        background:#f8f9fa;color:#495057}
        .sp{width:40px;height:40px;border:3px solid #dee2e6;border-top-color:#228be6;
        border-radius:50%;animation:spin .8s linear infinite;margin-bottom:16px}
        @keyframes spin{to{transform:rotate(360deg)}}
        h3{margin:0 0 8px;font-weight:500}p{margin:0;color:#868e96;font-size:14px}`;
      printWindow.document.head.appendChild(s);
      printWindow.document.title = "Generating Report…";
      printWindow.document.body.innerHTML =
        '<div class="sp"></div><h3>Generating Report PDF</h3><p>Please wait…</p>';
    }
    try {
      const html = await captureCurrentReportHtml();

      const blobUrl = await generatePdfBlobUrl(caseId, html);

      if (printWindow) {
        printWindow.location.href = blobUrl;
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);

      notifications.show({
        title: "Print Ready",
        message: "PDF opened. Press Ctrl+P (or ⌘P) to print.",
        color: "green",
      });
    } catch (err) {
      printWindow?.close();
      notifications.show({
        title: "Print Failed",
        message:
          err instanceof Error ? err.message : "Could not generate print PDF.",
        color: "red",
      });
    } finally {
      setIsPrinting(false);
    }
  }, [captureCurrentReportHtml, caseId]);

  useEffect(() => {
    if (opened || renderForCapture) return;
    resetPaginationState();
  }, [opened, renderForCapture, resetPaginationState]);

  // Build caseData and specimens from preview data for DocumentView
  const caseData = preview
    ? {
        id: caseId,
        caseNumber: preview.caseNumber,
        isFinalized: preview.signatureBlock.isSigned,
        signedOffAt: preview.signatureBlock.signedAt,
        dateReceived: preview.dateReceived,
        dateCollected: preview.dateCollected,
        sendingPracticeId: preview.sendingPracticeId,
        surgeryCenterLabId: preview.surgeryCenterLabId,
        statusName: preview.statusName,
        procedure: liveData?.procedure ?? preview.procedure,
        patient: {
          firstName: preview.patient.name.split(" ")[0],
          lastName: preview.patient.name.split(" ").slice(1).join(" "),
          dateOfBirth: preview.patient.dob,
          medicalRecordNumber: preview.patient.mrn,
        },
        physician: {
          firstName: preview.physician.name.split(" ")[0],
          lastName: preview.physician.name.split(" ").slice(1).join(" "),
        },
        signedOffBy: preview.signatureBlock.isSigned
          ? { fullName: preview.signatureBlock.signedBy }
          : undefined,
      }
    : null;

  const hasLiveSpecimens = liveData?.specimens && liveData.specimens.length > 0;

  const specimens = useMemo(() => {
    const previewSpecimens = getPreviewSpecimens(preview);
    return hasLiveSpecimens
      ? mergeAuthoritativeOrganMapData(
          liveData?.specimens || [],
          previewSpecimens
        )
      : previewSpecimens;
  }, [hasLiveSpecimens, liveData?.specimens, preview]);
  const hasMissingExpectedOrganMap = useMemo(
    () =>
      Boolean(
        effectiveDocumentViewData &&
          hasMissingExpectedOrganMapImage(
            effectiveDocumentViewData.template.templateContent.sections,
            specimens
          )
      ),
    [effectiveDocumentViewData, specimens]
  );
  const hasBlockingMissingOrganMap = useMemo(
    () => blockOnUnavailableOrganMaps && hasMissingExpectedOrganMap,
    [blockOnUnavailableOrganMaps, hasMissingExpectedOrganMap]
  );

  const diagnosisDataMap =
    hasLiveSpecimens && liveData?.diagnosisDataMap
      ? liveData.diagnosisDataMap
      : preview?.specimens?.length
      ? new Map(
          preview.specimens.map((s) => {
            const diag = preview.diagnoses.find(
              (d) => d.specimenLabel === s.number
            );
            return [
              s.id,
              {
                specimenId: s.id,
                diagnosisText: diag?.diagnosisText || "",
                microscopicDescription: diag?.microscopicDescription || "",
                category: diag?.category ?? null,
              },
            ];
          })
        )
      : new Map(
          (preview?.diagnoses || []).map((d, index) => [
            `preview-specimen-${index}`,
            {
              specimenId: `preview-specimen-${index}`,
              diagnosisText: d.diagnosisText || "",
              microscopicDescription: d.microscopicDescription || "",
              category: d.category ?? null,
            },
          ])
        );

  const grossingDataMap =
    hasLiveSpecimens && liveData?.grossingDataMap
      ? liveData.grossingDataMap
      : preview?.specimens?.length
      ? new Map(
          preview.specimens.map((s) => {
            const diag = preview.diagnoses.find(
              (d) => d.specimenLabel === s.number
            );
            return [
              s.id,
              {
                specimenId: s.id,
                grossDescription: diag?.grossDescription || "",
              },
            ];
          })
        )
      : new Map(
          (preview?.diagnoses || []).map((d, index) => [
            `preview-specimen-${index}`,
            {
              specimenId: `preview-specimen-${index}`,
              grossDescription: d.grossDescription || "",
            },
          ])
        );

  const previewClinicalHistory =
    liveData?.clinicalHistoryNotes ?? preview?.clinicalHistoryNotes ?? "";
  const previewIndications =
    liveData?.indications ?? preview?.indications ?? "";
  const previewImpressions =
    liveData?.impressions ?? preview?.impressions ?? "";
  const previewComment = liveData?.pathologistComment ?? "";
  const signedVersionCodeDisplays = deriveSignedVersionCodeDisplays(
    preview?.diagnoses,
    reportVersion
  );
  const effectiveLiveCptDisplay =
    signedVersionCodeDisplays?.cptCodes ?? liveData?.liveCptDisplay;
  const effectiveLiveIcdDisplay =
    signedVersionCodeDisplays?.icdCodes ?? liveData?.liveIcdDisplay;
  const inlineReportPhotos = useMemo<OrganMapPhoto[]>(() => {
    return selectedReportPhotos.map((image) => ({
      image,
      url: resolvedReportPhotoUrls.get(image.id),
      failed: failedImageIds.has(image.id),
      onLoadError: handleImageLoadError,
      onRetry: handleRetryImage,
    }));
  }, [
    failedImageIds,
    handleImageLoadError,
    handleRetryImage,
    resolvedReportPhotoUrls,
    selectedReportPhotos,
  ]);
  const partitionedReportPhotos = useMemo(
    () =>
      partitionAddendumReportPhotos(
        inlineReportPhotos,
        effectiveDocumentViewData
      ),
    [inlineReportPhotos, effectiveDocumentViewData]
  );
  const hasAttachments = reportDocumentFiles.length > 0;
  const hasBaseAttachments = baseReportDocumentFiles.length > 0;
  const attachmentPageProps = {
    reportImages: [],
    reportFiles: reportDocumentFiles,
    resolvedImageUrls,
    resolvedFileUrls,
    failedImageIds,
    failedFileIds: resolvedFailedFileIds,
    onImageLoadError: handleImageLoadError,
    onRetryImage: handleRetryImage,
    onFileLoadError: handleFileLoadError,
    onFileLoadComplete: handleFileLoadComplete,
    onRetryFile: handleRetryFile,
    allowExternalFileLinks: !renderForCapture,
    filePageCounts,
    headerSectionProps:
      headerSection && effectiveDocumentViewData && caseData
        ? {
            section: headerSection,
            documentViewData: effectiveDocumentViewData,
            caseData,
            specimens,
            diagnosisDataMap: diagnosisDataMap as any,
            grossingDataMap: grossingDataMap as any,
            clinicalHistoryNotes: previewClinicalHistory,
            indications: previewIndications,
            impressions: previewImpressions,
            pathologistComment: previewComment,
            pathologistComments,
            isReadOnly: true,
            pageIndex: 1,
          }
        : undefined,
  };
  const pageFooterIdentity = useMemo(() => {
    if (effectiveDocumentViewData?.legacyBaseline) {
      const header =
        effectiveDocumentViewData.legacyReportHeader ??
        effectiveDocumentViewData.legacyBaseline.header;
      return {
        patientName: historicalPatientName(header),
        caseNumber: header.caseNumber,
      };
    }
    if (!preview) return undefined;
    const patientNameParts = preview.patient.name.split(" ");
    return {
      patientName:
        resolvePatientLabelName({
          firstName: patientNameParts[0],
          lastName: patientNameParts.slice(1).join(" "),
        }) ?? "",
      caseNumber: preview.caseNumber || "",
    };
  }, [effectiveDocumentViewData, preview]);
  const compositePagination = useMemo(() => {
    if (
      !pageFooterIdentity ||
      mainReportPageCount === null ||
      resolvedSignedReportPageCount === null ||
      resolvedExternalReportPageCount === null
    ) {
      return null;
    }
    const interstitialPageCount = interstitialReportFile
      ? resolvedExternalReportPageCount
      : 0;
    const trailingPageCount = trailingReportFile
      ? resolvedExternalReportPageCount
      : 0;
    const baseAttachmentFirstPage = resolvedSignedReportPageCount + 1;
    const interstitialFirstPage =
      resolvedSignedReportPageCount + baseAttachmentPageCount + 1;
    const mainReportOffset =
      resolvedSignedReportPageCount +
      baseAttachmentPageCount +
      interstitialPageCount;
    const trailingFirstPage = mainReportOffset + mainReportPageCount + 1;
    const currentAttachmentFirstPage =
      mainReportOffset + mainReportPageCount + trailingPageCount + 1;
    const totalPages =
      mainReportOffset +
      mainReportPageCount +
      trailingPageCount +
      currentAttachmentPageCount;
    return {
      totalPages,
      mainReportOffset,
      baseAttachmentFirstPage,
      interstitialFirstPage,
      trailingFirstPage,
      currentAttachmentFirstPage,
      footerProps: pageFooterIdentity,
    };
  }, [
    baseAttachmentPageCount,
    currentAttachmentPageCount,
    interstitialReportFile,
    mainReportPageCount,
    pageFooterIdentity,
    resolvedExternalReportPageCount,
    resolvedSignedReportPageCount,
    trailingReportFile,
  ]);
  const currentAttachmentPageNumbering = useMemo(
    () =>
      compositePagination
        ? {
            footerProps: compositePagination.footerProps,
            firstPageNumber: compositePagination.currentAttachmentFirstPage,
            totalPages: compositePagination.totalPages,
          }
        : undefined,
    [compositePagination]
  );
  const documentViewProps: ComponentProps<typeof DocumentView> | null = caseData
    ? {
        caseData,
        specimens,
        diagnosisDataMap: diagnosisDataMap as any,
        grossingDataMap: grossingDataMap as any,
        clinicalHistoryNotes: previewClinicalHistory,
        indications: previewIndications,
        impressions: previewImpressions,
        pathologistComment: previewComment,
        pathologistComments,
        frozenInterpretation:
          liveData?.frozenInterpretation ?? preview?.frozenInterpretation,
        isReadOnly: true,
        paginate: true,
        noScrollWrapper: true,
        templateId: selectedTemplateId || undefined,
        reportVersion,
        documentViewDataOverride: effectiveDocumentViewData,
        liveCptDisplay: effectiveLiveCptDisplay,
        liveIcdDisplay: effectiveLiveIcdDisplay,
        diagnosisCategories,
        reviewSimulation: effectiveReviewRequired,
        reportPhotos:
          !composition &&
          (
            effectiveDocumentViewData as
              | (DocumentViewResponse & { attachedReport?: unknown })
              | undefined
          )?.attachedReport
            ? []
            : partitionedReportPhotos.main,
        addendumReportPhotos: partitionedReportPhotos.addendum,
        additionalPageCount:
          (trailingReportFile ? resolvedExternalReportPageCount ?? 0 : 0) +
          currentAttachmentPageCount,
        pageNumberOffset: compositePagination?.mainReportOffset ?? 0,
        onPaginationPending: handlePaginationPending,
        onPaginationSettled: handlePaginationSettled,
        onPaginationFailed: handlePaginationFailed,
      }
    : null;

  // Signal to the headless Puppeteer renderer that the document is fully laid
  // out (data fetched, S3 URLs resolved for every report image/file, fonts
  // ready, pagination settled). Once flipped, the renderer captures HTML.
  const allImagesResolved =
    selectedReportPhotos.length === 0 ||
    selectedReportPhotos.every(
      (image) =>
        resolvedReportPhotoUrls.has(image.id) && !failedImageIds.has(image.id)
    );
  const allFilesResolved =
    Boolean(composition) ||
    allReportDocumentFiles.length === 0 ||
    allReportDocumentFiles.every(
      (file) =>
        !isRenderableReportFile(file) ||
        (resolvedFileUrls.has(file.id) &&
          !resolvedFailedFileIds.has(file.id) &&
          loadedFileContentIds.has(file.id))
    );
  const attachmentListsSettled = renderMode
    ? Boolean(reportAttachmentsOverride) || renderAttachmentsQuery.isSuccess
    : filesQuery.isSuccess && imagesQuery.isSuccess;
  const attachmentQueriesSettled =
    !commentsQuery.isFetching &&
    !filesQuery.isFetching &&
    !imagesQuery.isFetching &&
    !renderAttachmentsQuery.isFetching &&
    imageUrlQueries.every((query) => !query.isFetching) &&
    fileUrlQueries.every((query) => !query.isFetching);
  const externalReportFileSettled =
    Boolean(composition) ||
    (!interstitialReportFile && !trailingReportFile) ||
    (externalReportFileLoaded && !externalReportFileFailed);

  const reviewDocumentSettled =
    (Boolean(previewDataOverride) || previewQuery.isSuccess) &&
    (Boolean(pathologistCommentsOverride) || commentsQuery.isSuccess) &&
    documentViewSettled &&
    categoriesSettled &&
    paginationSettled &&
    allImagesResolved &&
    allFilesResolved &&
    attachmentListsSettled &&
    attachmentQueriesSettled &&
    externalReportFileSettled &&
    !hasBlockingMissingOrganMap &&
    !attachmentListError;

  const handleReviewedSignOut = useCallback(async () => {
    if (!onSignOut) return;
    if (!effectiveReviewRequired) {
      await onSignOut();
      return;
    }
    if (!preview?.review || !reviewDocumentSettled) return;
    setIsAcknowledgingReview(true);
    setReviewError(null);
    try {
      const evidence = await (
        onAcknowledgeReview ||
        ((version) =>
          acknowledgeReportPreview(caseId, version, documentViewData!))
      )(preview.review.contentVersion);
      await onSignOut(evidence);
    } catch (error) {
      if (error instanceof ReportContentChangedError) {
        resetPaginationState();
        if (onReviewContentChanged) await onReviewContentChanged();
        else await refetchPreview();
      }
      setReviewError({
        kind: error instanceof ReportContentChangedError ? "info" : "error",
        title:
          error instanceof ReportContentChangedError
            ? "Report updated"
            : "Review not recorded",
        message:
          error instanceof Error
            ? error.message
            : "The report review could not be recorded. Check your connection and try again.",
      });
    } finally {
      setIsAcknowledgingReview(false);
    }
  }, [
    caseId,
    documentViewData,
    onAcknowledgeReview,
    onReviewContentChanged,
    onSignOut,
    preview?.review,
    refetchPreview,
    resetPaginationState,
    reviewDocumentSettled,
    effectiveReviewRequired,
  ]);

  const captureReady =
    !!renderForCapture &&
    !!caseData &&
    (Boolean(previewDataOverride) || !isLoading) &&
    !isCategoriesLoading &&
    !!effectiveDocumentViewData &&
    (Boolean(pathologistCommentsOverride) || commentsQuery.isSuccess) &&
    documentViewSettled &&
    categoriesSettled &&
    attachmentListsSettled &&
    attachmentQueriesSettled &&
    !attachmentListError &&
    !hasBlockingMissingOrganMap &&
    allImagesResolved &&
    allFilesResolved &&
    paginationSettled;

  useEffect(() => {
    if (renderForCapture && hasBlockingMissingOrganMap) {
      signalInternalRenderFailure();
      return;
    }
    if (!captureReady) return;
    let cancelled = false;

    async function waitAndSignal() {
      // paginationSettled (a captureReady gate) already guarantees:
      //   • Times New Roman loaded (fontsReady === true in DocumentView)
      //   • sectionHeights measured with correct font metrics
      //   • pages array computed from those heights
      // One RAF lets the final pagination render paint to the DOM.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (cancelled) return;

      const reportRoot = reportDocumentRef.current;
      if (!reportRoot) {
        signalInternalRenderFailure();
        return;
      }
      let html: string;
      try {
        html = await captureReportHtml(reportRoot, {
          pageStylesheets: collectPageStylesheets(),
        });
      } catch {
        signalInternalRenderFailure();
        return;
      }
      if (cancelled) return;

      // Expose for Puppeteer to read via page.evaluate() instead of page.content().
      (window as any).reportHtmlCapture = html;
      document.documentElement.dataset.renderComplete = "true";
    }

    waitAndSignal();

    return () => {
      cancelled = true;
      delete document.documentElement.dataset.renderComplete;
    };
  }, [captureReady, hasBlockingMissingOrganMap, renderForCapture]);

  const composedDocument =
    composition && documentViewProps && pageFooterIdentity ? (
      <ReportCompositionDocument
        key={reviewContentKey || JSON.stringify(composition)}
        caseId={caseId}
        composition={composition}
        documentProps={documentViewProps}
        internal={renderMode}
        footer={pageFooterIdentity}
        headerSectionProps={attachmentPageProps.headerSectionProps}
        onSettled={handlePaginationSettled}
        onPending={handlePaginationPending}
        onError={handlePaginationFailed}
      />
    ) : null;

  if (renderForCapture) {
    if (!documentViewProps) return null;
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: REPORT_PAGE_LAYOUT.widthPx,
          backgroundColor: "#fff",
        }}
      >
        <div ref={reportDocumentRef} className="report-preview-document">
          {composedDocument || (
            <>
              <DocumentView {...documentViewProps} />
              {hasAttachments && (
                <ReportAttachmentPages
                  {...attachmentPageProps}
                  pageNumbering={currentAttachmentPageNumbering}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size={920}
      centered
      styles={{
        content: { display: "flex", flexDirection: "column" },
        body: { flex: 1, minHeight: 0, overflow: "hidden" },
      }}
    >
      <Stack gap="md" style={{ height: "80vh", minHeight: 0 }}>
        {canSelectTemplate && activeTemplates.length > 0 && (
          <Group justify="flex-end" align="center">
            <Group gap="xs" align="center">
              <Select
                size="xs"
                data={activeTemplates.map((t: any) => ({
                  value: t.id,
                  label: t.name,
                }))}
                value={selectedTemplateId || null}
                onChange={handleTemplateChange}
                styles={{ input: { fontSize: 12, minHeight: 28 } }}
                w={200}
              />
            </Group>
          </Group>
        )}

        {clinicalSideEffectNotice && (
          <Alert
            color="yellow"
            variant="light"
            title="Additional stains on sign-off"
            role="alert"
            mb="sm"
          >
            {clinicalSideEffectNotice}
          </Alert>
        )}
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : error ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            variant="light"
            title="Report preview unavailable"
            role="alert"
          >
            The report preview could not be loaded. Try again.
          </Alert>
        ) : caseData && documentViewProps ? (
          <>
            {reviewError && (
              <Alert
                icon={
                  reviewError.kind === "info" ? (
                    <IconInfoCircle size={18} />
                  ) : (
                    <IconAlertCircle size={18} />
                  )
                }
                color={reviewError.kind === "info" ? "blue" : "red"}
                variant="light"
                title={reviewError.title}
                role={reviewError.kind === "info" ? "status" : "alert"}
              >
                {reviewError.message}
              </Alert>
            )}
            {attachmentListError && (
              <Alert
                icon={<IconAlertCircle size={18} />}
                color="red"
                title="Report attachments unavailable"
              >
                <Stack gap="xs" align="flex-start">
                  <Text size="sm">{REPORT_IMAGE_NOT_AVAILABLE_MESSAGE}</Text>
                  <Button
                    variant="light"
                    color="red"
                    onClick={handleRetryAttachmentList}
                    mih={44}
                  >
                    Retry attachments
                  </Button>
                </Stack>
              </Alert>
            )}
            {hasBlockingMissingOrganMap && (
              <Alert
                icon={<IconAlertCircle size={18} />}
                color="red"
                title="Report organ map unavailable"
              >
                <Stack gap="xs" align="flex-start">
                  <Text size="sm">
                    The selected report expects an organ map, but its image is
                    not available. Retry before reviewing or signing out.
                  </Text>
                  <Button
                    variant="light"
                    color="red"
                    onClick={handleRetryReportData}
                    mih={44}
                  >
                    Retry Report Data
                  </Button>
                </Stack>
              </Alert>
            )}
            {/* Grey desk background that scrolls; white Letter-size paper inside */}
            <ScrollArea
              type="auto"
              style={{
                backgroundColor: "white",
                flex: 1,
                minHeight: 0,
              }}
              styles={{ viewport: { padding: "16px" } }}
            >
              <div ref={reportDocumentRef} className="report-preview-document">
                {composedDocument || (
                  <>
                    {effectiveSignedDocumentViewData && documentViewProps && (
                      <DocumentView
                        {...documentViewProps}
                        reportPhotos={partitionedReportPhotos.main}
                        documentViewDataOverride={
                          effectiveSignedDocumentViewData
                        }
                        additionalPageCount={
                          compositePagination && signedReportPageCount !== null
                            ? compositePagination.totalPages -
                              signedReportPageCount
                            : 0
                        }
                        pageNumberOffset={0}
                        onPaginationPending={handleSignedPaginationPending}
                        onPaginationSettled={handleSignedPaginationSettled}
                        onPaginationFailed={undefined}
                      />
                    )}
                    {hasBaseAttachments && (
                      <ReportAttachmentPages
                        {...attachmentPageProps}
                        reportFiles={baseReportDocumentFiles}
                        pageNumbering={
                          compositePagination
                            ? {
                                footerProps: compositePagination.footerProps,
                                firstPageNumber:
                                  compositePagination.baseAttachmentFirstPage,
                                totalPages: compositePagination.totalPages,
                              }
                            : undefined
                        }
                      />
                    )}
                    {interstitialReportFile && (
                      <ReportFilePage
                        key={`${interstitialReportFile.file.id}:${externalReportFileAttempt}`}
                        file={interstitialReportFile.file}
                        url={interstitialReportFile.url}
                        failed={externalReportFileFailed}
                        onLoadComplete={handleExternalReportFileLoadComplete}
                        onLoadError={handleExternalReportFileLoadError}
                        onRetry={handleRetryExternalReportFile}
                        allowExternalLink={false}
                        headerSectionProps={
                          attachmentPageProps.headerSectionProps
                        }
                        footerProps={compositePagination?.footerProps}
                        firstPageNumber={
                          compositePagination?.interstitialFirstPage
                        }
                        totalPages={compositePagination?.totalPages}
                      />
                    )}
                    <DocumentView {...documentViewProps} />

                    {trailingReportFile && (
                      <ReportFilePage
                        key={`${trailingReportFile.file.id}:${externalReportFileAttempt}`}
                        file={trailingReportFile.file}
                        url={trailingReportFile.url}
                        failed={externalReportFileFailed}
                        onLoadComplete={handleExternalReportFileLoadComplete}
                        onLoadError={handleExternalReportFileLoadError}
                        onRetry={handleRetryExternalReportFile}
                        allowExternalLink={false}
                        headerSectionProps={
                          attachmentPageProps.headerSectionProps
                        }
                        footerProps={compositePagination?.footerProps}
                        firstPageNumber={compositePagination?.trailingFirstPage}
                        totalPages={compositePagination?.totalPages}
                      />
                    )}

                    {hasAttachments && (
                      <ReportAttachmentPages
                        {...attachmentPageProps}
                        pageNumbering={currentAttachmentPageNumbering}
                      />
                    )}
                  </>
                )}
              </div>
            </ScrollArea>

            {!readOnly && (
              <>
                <Divider />

                <Group justify="space-between" style={{ flexShrink: 0 }}>
                  <Group gap="xs">
                    {onBack && (
                      <Button
                        variant="default"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={onBack}
                      >
                        Back
                      </Button>
                    )}
                    {showPrintAction && (
                      <Button
                        variant="light"
                        leftSection={<IconPrinter size={16} />}
                        loading={isPrinting}
                        onClick={handlePrint}
                        disabled={!paginationSettled}
                      >
                        Print Preview
                      </Button>
                    )}
                  </Group>

                  <Group>
                    {onSignOut &&
                      (allowSignOutWhenFinalized ||
                        !preview?.signatureBlock.isSigned) && (
                        <Button
                          color="green"
                          leftSection={<IconCheck size={16} />}
                          onClick={handleReviewedSignOut}
                          loading={isAcknowledgingReview || signOutPending}
                          disabled={
                            effectiveReviewRequired && !reviewDocumentSettled
                          }
                        >
                          {signOutLabel ||
                            (effectiveReviewRequired
                              ? "Sign Out Reviewed Report"
                              : "Sign Out")}
                        </Button>
                      )}
                  </Group>
                </Group>
              </>
            )}
          </>
        ) : null}
      </Stack>
    </Modal>
  );
}
